<template>
  <div
    class="rounded-2xl border-2 border-black-dark bg-white p-2 shadow-blue-hard-sm transition-all hover:brightness-105 dark:bg-kekkle-dark-light dark:shadow-purple-hard-sm"
  >
    <div class="flex flex-nowrap justify-between">
      <div class="flex flex-nowrap items-center">
        <small-user-thumb class="shrink-0" :uuid="uuid" />
        <div class="mx-3">
          <h2 class="text-xl font-medium dark:text-slate-200">
            {{ member.name }}
          </h2>
          <p class="w-max rounded-3xl px-4" :class="memberClass">
            {{ memberText }}
          </p>
        </div>
      </div>
      <trash-icon
        v-if="canDelete"
        :class="
          canDelete
            ? 'cursor-pointer text-black-dark hover:text-kekkle-red dark:text-slate-200 dark:hover:text-kekkle-red-light'
            : 'cursor-not-allowed text-gray-300 dark:text-slate-700'
        "
        class="w-6 shrink-0 transition-colors"
        @click="deleteButton"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import SmallUserThumb from "./SmallUserThumb.vue";
import { TrashIcon } from "@heroicons/vue/outline";
import { useGroupsStore } from "@/stores/modules/groups/groups";

type Member = {
  name: string;
  permission: "admin" | "organiser" | "member";
  [key: string]: any;
};

type Props = {
  member: Member;
  uuid: string;
};

type Emits = {
  (e: "deleteMember"): void;
};

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const { t } = useI18n();
const groupsStore = useGroupsStore();

const memberClass = computed(() => {
  switch (props.member.permission) {
    case "admin":
      return "bg-green-200 text-green-600 dark:bg-green-900 dark:text-green-300";
    case "organiser":
      return "bg-orange-200 text-orange-600 dark:bg-orange-900 dark:text-orange-300";
    case "member":
      return "bg-purple-200 text-purple-600 dark:bg-purple-900 dark:text-purple-300";
    default:
      return "";
  }
});

const memberText = computed(() => {
  switch (props.member.permission) {
    case "admin":
      return t("global.roles.admin");
    case "organiser":
      return t("global.roles.organiser");
    case "member":
      return t("global.roles.member");
    default:
      return "";
  }
});

const canDelete = computed(() => {
  return groupsStore.isAdmin && props.member.permission !== "admin";
});

function deleteButton(): void {
  if (canDelete.value) {
    emit("deleteMember");
  }
}
</script>
